import { useState } from 'react'

import { Box, Button, CheckBox, Text, TextSpinner } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import i18n from 'modules/core/lib/i18n'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { getEmployeeCountCopy } from 'modules/shift_management/lib/getDescription'
import shiftManagementCounts from 'modules/shift_management/lib/shiftManagementCounts'
import type { RestDay, Shift } from 'modules/shift_management/lib/types'

import getDeleteModalTranslations from './getDeleteModalTranslations'
import useBulkDestroy from './useBulkDestroy'

interface Props {
  shifts: Shift[]
  restDays: RestDay[]
  onClose: () => void
}

const DeleteSelectedModal = ({ shifts, restDays, onClose }: Props) => {
  const [{ hasFeatureFlag, hasFeatureEntitlement }] = useEnabledFeatures()
  const { shiftsCount, restDaysCount, employeeCount } = shiftManagementCounts(shifts, restDays)
  const { title, button, body } = getDeleteModalTranslations(shiftsCount, restDaysCount)
  const employeesCounter = getEmployeeCountCopy(employeeCount)
  const [destroyBackupShifts, setDestroyBackupShifts] = useState<boolean>(false)
  const [destroyAutomaticOnly, setDestroyAutomaticOnly] = useState<boolean>(false)
  const onCheckboxClick = () => setDestroyBackupShifts(!destroyBackupShifts)
  const { onDestroy, submitting } = useBulkDestroy({
    shifts,
    restDays,
    destroyBackupShifts,
    destroyAutomaticOnly,
  })

  const hasBackupShifts = hasFeatureFlag('shift_management_backup_shifts')
  const isAutomationEnabled = hasFeatureEntitlement(Features.shift_management_automatic_planning)

  return (
    <Modal
      illustration="delete-cross"
      title={title}
      description={
        <>
          {submitting && <TextSpinner text={i18n.t('shift_management.actions.deleting_shifts')} />}
          {!submitting &&
            i18n.tView('shift_management.delete_selected', {
              count: <Text.Normal>{body}</Text.Normal>,

              total: (
                <Text.Normal>
                  {employeeCount}&nbsp;{employeesCounter}
                </Text.Normal>
              ),
            })}
        </>
      }
      onSubmit={onDestroy}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="negative" disabled={submitting} submit>
            {button}
          </Button>
        </>
      }
    >
      {!submitting && hasBackupShifts && (
        <Box flexDirection="row" gap="s8">
          <Box paddingY="s2">
            <CheckBox checked={destroyBackupShifts} onClick={onCheckboxClick} />
          </Box>
          <Text.Normal size="200" weight="medium">
            {i18n.t('shift_management.backup_shifts.delete')}
          </Text.Normal>
        </Box>
      )}

      {!submitting && isAutomationEnabled && (
        <Box flexDirection="row" gap="s8">
          <Box paddingY="s2">
            <CheckBox
              checked={destroyAutomaticOnly}
              onClick={() => setDestroyAutomaticOnly(!destroyAutomaticOnly)}
            />
          </Box>
          <Text.Normal size="200" weight="medium">
            {i18n.t('shift_management.coverage_section.automation.delete')}
          </Text.Normal>
        </Box>
      )}
    </Modal>
  )
}

export default observer(DeleteSelectedModal)
